import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Users, Building2, Clock, CheckCircle2 } from "lucide-react";
import { TRAINING_PROGRAMS } from "@/lib/data";
import { useSEO } from "@/hooks/useSEO";

const HERO_IMG = "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function TrainingAcademy() {
  useSEO({
    title: "Corporate Training — Endur · ETRM · CTRM · Executive Briefings | EnPrex Services",
    description: "A small, curated corporate training practice for enterprise clients — Endur, ETRM and CTRM foundations plus executive briefings delivered by practising senior consultants.",
    path: "/training",
  });
  return (
    <div data-testid="training-page">
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3A]/60 via-[#050A14]/85 to-[#050A14]" />
        </div>
        <div className="container-x relative pt-16 pb-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-6 font-mono">— Corporate Training</div>
            <h1 className="font-display text-5xl md:text-7xl font-light text-white tracking-tighter leading-[0.98] max-w-5xl">
              A small, curated <span className="italic">corporate training</span> practice.
            </h1>
            <p className="mt-8 text-lg text-slate-300 max-w-3xl font-light leading-relaxed">
              Alongside our advisory, implementation and managed services, we deliver a focused set of corporate training programs for enterprise clients. Every program is functional, business-oriented, and delivered by practising senior consultants — never a career-trainer.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="border-b border-white/5">
        <div className="container-x py-14 grid md:grid-cols-3 gap-8">
          {[
            { Icon: Building2, t: "For teams & enterprises", d: "Corporate training for teams and enterprise clients." },
            { Icon: Users, t: "Functional & executive", d: "Business-oriented content — not deep technical or scripting workshops." },
            { Icon: Clock, t: "Concise formats", d: "Half-day, one-day, or two-to-four-day modular programs." },
          ].map(({ Icon, t, d }, i) => (
            <motion.div key={t} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
              <Icon className="text-[#D4AF37] mb-4" size={24} strokeWidth={1.4} />
              <h3 className="font-display text-white text-lg font-medium">{t}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="container-x py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 max-w-4xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-6 font-mono">— Our Programs</div>
          <h2 className="font-display text-4xl md:text-6xl font-light text-white tracking-tighter leading-[1.05]">
            Four programs. <span className="italic">Enterprise clients</span> only.
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-px bg-white/5">
          {TRAINING_PROGRAMS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 2) * 0.06 }}
                className="bg-[#050A14] p-10 group hover:bg-[#0B1F3A]/40 transition-all"
                data-testid={`program-${p.slug}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <Icon className="text-[#D4AF37]" size={28} strokeWidth={1.2} />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    {p.format}
                  </span>
                </div>
                <h3 className="font-display text-xl text-white font-medium leading-tight">{p.title}</h3>
                <p className="text-sm text-slate-400 mt-3 leading-relaxed">{p.short}</p>
                <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-slate-400 font-mono">
                    <Users size={12} className="text-[#D4AF37]" /> {p.audience}
                  </div>
                  <Link to={`/training/${p.slug}`} data-testid={`program-link-${p.slug}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] font-semibold text-slate-400 group-hover:text-[#D4AF37] transition-colors font-mono">
                    Explore <ArrowUpRight size={12} />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="container-x pb-24">
        <div className="border border-white/5 bg-white/[0.02] p-10 md:p-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-6 font-mono">— For Organizations</div>
            <h2 className="font-display text-3xl md:text-5xl font-light text-white tracking-tighter leading-[1.05]">
              A training partner your <span className="italic">L&D leadership</span> will endorse.
            </h2>
          </div>
          <div className="lg:col-span-7 grid md:grid-cols-2 gap-6">
            {[
              "Curricula calibrated to your platform version, modules and processes",
              "Delivered on-site or virtual — global time zones",
              "Instructors are practising senior consultants, not career trainers",
              "Business-oriented content only — no deep technical or scripting deep-dives",
              "Post-training reference material and knowledge transfer plans",
              "Reserved for corporate teams and enterprise clients",
            ].map((point) => (
              <div key={point} className="flex items-start gap-3">
                <CheckCircle2 className="text-[#D4AF37] mt-0.5 flex-shrink-0" size={18} />
                <p className="text-sm text-slate-300 leading-relaxed">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x pb-24">
        <div className="border border-[#D4AF37]/25 bg-gradient-to-br from-[#0B1F3A]/60 to-[#050A14] p-10 md:p-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-3xl md:text-4xl font-light text-white tracking-tight leading-tight">
              Request corporate training for your organization.
            </h2>
            <p className="mt-4 text-slate-300 font-light max-w-2xl">
              All enquiries are managed exclusively through our secure enquiry form. A senior consultant will respond within two business days with a tailored proposal.
            </p>
          </div>
          <div className="lg:col-span-4">
            <Link to="/contact" data-testid="training-cta-request" className="w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#0B1F3A] px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#FDE047] transition-colors">
              Request Corporate Training <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
